import { useMemo, useCallback } from "react";
import {
  PARAMETER_IDENTITE_CSE_PATH,
  PARAMETER_STUDIO_PATH,
  PARAMETER_COORDONNEES_PATH,
  PARAMETER_INFORMATIONS_PATH,
  PARAMETER_SITES_PATH,
  PARAMETER_WELCOME_EMAIL_PATH,
  PARAMETER_DATA_MANAGEMENT_PATH,
  PARAMETER_BENEFICIARIES_PATH,
  PARAMETER_PATH,
  PARAMETER_PROOF_DOCUMENTS_PATH,
  PARAMETER_REPRESENTATIVE_ROLES_PATH,
  RULES_PAGE_PATH,
  PARAMETER_EMAIL_SIGNATURE_PATH,
  PARAMETER_MODULES_CONFIG_PATH,
  PARAMETER_DIRECT_DEBIT_PATH,
  PARAMETER_SUBSCRIPTION_INVOICES_PATH,
} from "@/shared/configs/routes.config";
import { useOrganizationFeatures, useOrganizationPlan } from "@/shared/hooks/useOrganizationFeatures";
import { useRepresentativePermissions } from "@/shared/hooks/useRepresentativePermissions";
import type { PermissionFeatureCode } from "@/shared/types/representativePermissions";
import { isDashboardCommunicationPlan } from "@/shared/utils/dashboardCommunicationPlan";

export type Tab = {
  key: string
  label: string
  path: string
};

type TabDefinition = {
  featureEnabled: boolean
  canRead: boolean
  tab: Tab
  insertAfter?: string
};

const buildTabs = (definitions: TabDefinition[]): Tab[] => {
  const tabs: Tab[] = [];

  for (const def of definitions) {
    if (!def.featureEnabled || !def.canRead) continue;

    if (def.insertAfter) {
      const targetIndex = tabs.findIndex((t) => t.key === def.insertAfter);
      if (targetIndex !== -1) {
        tabs.splice(targetIndex + 1, 0, def.tab);
        continue;
      }
    }

    tabs.push(def.tab);
  }

  return tabs;
};

const getTabKeyFromPath = (pathname: string, tabs: Tab[]): string | null => {
  for (const tab of tabs) {
    if (pathname.startsWith(tab.path)) return tab.key;
  }
  if (pathname === PARAMETER_PATH) return tabs[0]?.key ?? null;
  return null;
};

export const useParametersNavigation = () => {
  const {
    serviceTheme,
    organizationParameters,
    serviceBeneficiaries,
    serviceCustomAttribute,
    serviceProofDocuments,
    rulesEngine,
    serviceCommunication,
    serviceRepresentativeRoles,
  } = useOrganizationFeatures();
  const { canRead, canReadRoles, permissions } = useRepresentativePermissions();
  const { plan } = useOrganizationPlan();
  const isDashboardCommunication = isDashboardCommunicationPlan(plan?.code);
  const directDebitManage = plan?.direct_debit_manage === true;

  const safeCanRead = useCallback(
    (code: PermissionFeatureCode): boolean =>
      permissions === null ? true : canRead(code),
    [permissions, canRead],
  );
  const safeCanReadRoles = permissions === null ? true : canReadRoles;

  const tabs = useMemo(() => {
    const definitions: TabDefinition[] = [
      {
        featureEnabled: serviceTheme,
        canRead: safeCanRead("service_theme"),
        tab: { key: "studio", label: "Yeati Studio", path: PARAMETER_STUDIO_PATH },
      },
      {
        featureEnabled: organizationParameters,
        canRead: safeCanRead("organization_parameters"),
        tab: { key: "identite-du-cse", label: "Identité du CSE", path: PARAMETER_IDENTITE_CSE_PATH },
      },
      {
        featureEnabled: organizationParameters,
        canRead: safeCanRead("organization_parameters"),
        tab: { key: "coordonnees", label: "Coordonnées", path: PARAMETER_COORDONNEES_PATH },
      },
      {
        featureEnabled: organizationParameters,
        canRead: safeCanRead("organization_parameters"),
        tab: { key: "informations", label: "Informations", path: PARAMETER_INFORMATIONS_PATH },
      },
      {
        featureEnabled: organizationParameters,
        canRead: safeCanRead("organization_parameters"),
        tab: { key: "gestion-des-sites", label: "Gestion des sites", path: PARAMETER_SITES_PATH },
      },
      {
        featureEnabled: serviceCommunication,
        canRead: safeCanRead("service_communication"),
        tab: { key: "signature-email", label: "Signature d'email", path: PARAMETER_EMAIL_SIGNATURE_PATH },
        insertAfter: "informations",
      },
      {
        featureEnabled: serviceCustomAttribute,
        canRead: safeCanRead("service_custom_attribute"),
        tab: { key: "gestion-des-donnees", label: "Gestion des données", path: PARAMETER_DATA_MANAGEMENT_PATH },
      },
      {
        featureEnabled: serviceBeneficiaries,
        canRead: safeCanRead("service_beneficiaries"),
        tab: { key: "ayants-droit", label: "Ayants droit", path: PARAMETER_BENEFICIARIES_PATH },
      },
      {
        featureEnabled: serviceProofDocuments,
        canRead: safeCanRead("service_proof_document"),
        tab: { key: "justificatifs", label: "Gestion des justificatifs", path: PARAMETER_PROOF_DOCUMENTS_PATH },
      },
      {
        featureEnabled: organizationParameters,
        canRead: safeCanRead("organization_parameters"),
        tab: { key: "email-bienvenue", label: "E-Mail de bienvenue", path: PARAMETER_WELCOME_EMAIL_PATH },
      },
      {
        featureEnabled: serviceRepresentativeRoles,
        canRead: safeCanReadRoles,
        tab: { key: "representative-roles", label: "Rôles & Permissions", path: PARAMETER_REPRESENTATIVE_ROLES_PATH },
      },
      {
        featureEnabled: organizationParameters,
        canRead: safeCanRead("organization_parameters"),
        tab: { key: "config-modules", label: "Config. modules", path: PARAMETER_MODULES_CONFIG_PATH },
      },
      {
        featureEnabled: rulesEngine,
        canRead: safeCanRead("rules_engine"),
        tab: { key: "regles", label: "Règles", path: RULES_PAGE_PATH },
      },
      {
        // The Yeati subscription invoices, above the debit: the same administrators only.
        featureEnabled: directDebitManage,
        canRead: true,
        tab: { key: "factures", label: "Factures", path: PARAMETER_SUBSCRIPTION_INVOICES_PATH },
      },
      {
        // The Yeati subscription debit: the system role « Administrateur » only, whatever the plan.
        featureEnabled: directDebitManage,
        canRead: true,
        tab: { key: "prelevement", label: "Prélèvement", path: PARAMETER_DIRECT_DEBIT_PATH },
      },
    ];

    return buildTabs(definitions);
  }, [
    serviceTheme,
    organizationParameters,
    serviceBeneficiaries,
    serviceCustomAttribute,
    serviceProofDocuments,
    rulesEngine,
    serviceCommunication,
    serviceRepresentativeRoles,
    safeCanRead,
    safeCanReadRoles,
    isDashboardCommunication,
    directDebitManage,
  ]);

  const resolveTabKeyFromPath = useCallback(
    (pathname: string) => getTabKeyFromPath(pathname, tabs),
    [tabs],
  );

  return {
    tabs,
    getTabKeyFromPath: resolveTabKeyFromPath,
    serviceBeneficiaries,
    serviceCustomAttribute,
  };
};
